import React, { useState, useEffect } from "react";
import greenBin from "../../../upload/bin/green.png";
import yellowBin from "../../../upload/bin/yellow.png";
import redBin from "../../../upload/bin/red.png";
import "./result-box.css";

export default function ResultBox({ result, error, capturedImage, onBack }) {
  const [activeCard, setActiveCard] = useState(1);
  const [confirmed, setConfirmed] = useState(false);
  const [vw, setVw] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);

  // keep track of viewport width for responsive stacking math
  useEffect(() => {
    const onResize = () => setVw(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  useEffect(() => {
    const timer = setTimeout(() => {
      window.location.reload();
    }, 10000); // 10000ms = 10 giây

    // Cleanup nếu component unmount trước khi timeout
    return () => clearTimeout(timer);
  }, []);

  const actualResult = result?.result || result;
  const binImages = { green: greenBin, yellow: yellowBin, red: redBin };

  const steps = [];
  if (actualResult) {
    for (let i = 1; i <= 10; i++) {
      if (actualResult[`Buoc${i}`]) steps.push(i);
    }
  }
  const showStepTitle = steps.length > 1; // chỉ hiện "Bước X" khi >=2 bước

  useEffect(() => {
    if (!confirmed || steps.length <= 1) return;
    // ensure the activeCard is valid when we start
    if (!steps.includes(activeCard)) setActiveCard(steps[0]);
    const period = Math.max(200, Math.floor(9000 / steps.length));
    const id = setInterval(() => {
      setActiveCard((prev) => {
        const idx = steps.indexOf(prev);
        if (idx === -1) return steps[0];
        const nextIdx = idx + 1;
        // if there is a next step, advance to it
        if (nextIdx < steps.length) {
          return steps[nextIdx];
        }
        // otherwise we've reached the final step: clear interval and reload
        clearInterval(id);
        // keep the last card visible for one period, then reload
        setTimeout(() => window.location.reload(), period);
        return prev; // keep showing the final card until reload
      });
    }, period);
    return () => clearInterval(id);
  }, [confirmed, steps.join(",")]);

  if (!result && !error) return null;

  // Khi xác nhận, hiển thị mỗi bước như 1 card xếp chồng nhau
  if (confirmed) {
    if (steps.length === 0) {
      return (
        <div className="result-box">
          <div className="no-steps">Không có hướng dẫn cho loại rác này.</div>
        </div>
      );
    }


    // orderedSteps: rotate steps so activeCard is first, preserving original order
    const orderedSteps = (function () {
      if (!steps.includes(activeCard)) return steps;
      const idx = steps.indexOf(activeCard);
      return steps.slice(idx).concat(steps.slice(0, idx));
    })();

    return (
      <div className="result-box">
        <div className="stack-container">
          {orderedSteps.map((num, idx) => {
            const binKey = `bin_buoc${num}`;
            const binColor = actualResult[binKey]; // e.g. 'green' | 'red' | 'yellow'
            const binImgSrc = binImages[binColor];

            // stacking offset and scale (responsive to viewport)
            let txBase = 200, tyBase = 1, sxStep = 0.05, syStep = 0.1;
            if (vw <= 1024 && vw >= 768) {
              // iPad range
              txBase = 120;
              tyBase = 10;
              sxStep = 0.012;
              syStep = 0.045;
            } else if (vw < 768) {
              // smaller phones
              txBase = 80;
              tyBase = 8;
              sxStep = 0.01;
              syStep = 0.03;
            } else if (vw < 1400) {
              // medium desktops
              txBase = 180;
              tyBase = 11;
              sxStep = 0.015;
              syStep = 0.05;
            }

            const tx = idx * txBase;
            const ty = -idx * tyBase;
            // small responsive left shift so the whole stack sits slightly left
            // of center (helps match design where cards peek from the right)
            let leftShift = 0;
            if (vw >= 1400) leftShift = -120;
            else if (vw >= 1024) leftShift = -100;
            else if (vw >= 768) leftShift = -80;
            else leftShift = -40;
            // make later cards slightly narrower and shorter
            const scaleX = 1 - idx * sxStep;
            const scaleY = 1 - idx * syStep;
            const z = orderedSteps.length - idx; // front card should be on top

            // choose card background by bin color as an approximation to the design
            let cardBg = "#ffd95a"; // default yellow
            if (binColor === "red") cardBg = "#1fb6ff"; // blue-ish behind in reference
            if (binColor === "yellow") cardBg = "#5fc775"; // green-ish
            if (binColor === "green") cardBg = "#ffd95a"; // yellow front when green bin

            return (
              <div
                key={num}
                className={`stacked-card ${idx === 0 ? "front" : "back"}`}
                onClick={() => setActiveCard(num)}
                style={{
                  transform: `translateX(calc(-50% + ${tx}px + ${leftShift}px)) translateY(calc(-50% + ${ty}px)) scale(${scaleX}, ${scaleY})`,
                  zIndex: z,
                  background: cardBg,
                }}
              >
                <div className="card-inner">
                  <div className="card-left">
                    {showStepTitle && <div className="card-step">Bước {num}</div>}
                    <div className="card-text">{actualResult[`Buoc${num}`]}</div>
                  </div>
                  <div className="card-right">
                    {binImgSrc && <img src={binImgSrc} alt={`bin ${binColor}`} className="card-bin-img" />}
                    <div className="bin-shelf" />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // Default (not confirmed) - show confirm UI only
  return (
    <div className="result-box">
      {error ? (
        <div className="result-error">{error}</div>
      ) : (
        <div className="confirm-wrap">
          <div className="confirm-title">
            <div className="result-confirm">Đây là</div>
            <div className="result-confirm">
              {actualResult.LoaiRac ? actualResult.LoaiRac : "Không nhận diện được"}
            </div>
          </div>

          <div className="polaroid-wrap">
            <div className="polaroid-frame">
              {capturedImage ? (
                <img src={capturedImage} alt="captured" className="polaroid-img" />
              ) : (
                <div className="polaroid-placeholder" />
              )}
            </div>

            <div className="magnifier" aria-hidden>
              <svg xmlns="http://www.w3.org/2000/svg" width="100" height="100" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"></circle><path d="m21 21-4.3-4.3"></path></svg>
            </div>
          </div>

          <div className="confirm-actions-scan">
            <button className="btn btn-yes large" onClick={() => setConfirmed(true)}>
              Đúng rồi nhé
            </button>
            <button className="btn btn-no large" onClick={onBack}>
              Sai rồi phải quét lại thôi!!!
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
